import { createClient } from '@supabase/supabase-js'

export const supabase = createClient(
  'https://bwchvintnzqqbrpxhovq.supabase.co',
  'sb_publishable_9dlZ0__K5XxbPEAsUFsMWQ_8RoUt-il',
  { global: { fetch: window.fetch.bind(window) } }
)

export type Template = {
  id: string
  name: string
  report_type: string
  blocks: any[]
  pdf_template: any | null
  defaults: Record<string, any>
  parent_template_id: string | null
  folder: string
  created_at: string
  updated_at: string
}

export type TemplateFolder = {
  id: string
  org_id?: string | null
  path: string
  created_at: string
}

export type Report = {
  id: string
  template_id: string | null
  template_snapshot: any | null
  report_type: string
  job_no: string | null
  report_date: string | null
  client: string | null
  vessel: string | null
  data: Record<string, any>
  parent_report_id: string | null
  created_at: string
}

export type Employee = {
  id: string
  org_id?: string | null
  full_name: string
  email: string | null
  phone: string | null
  job_title: string | null
  department: string | null
  location: string | null
  nationality: string | null
  date_of_birth: string | null
  fin_number: string | null
  passport_number: string | null
  passport_expiry: string | null
  profile_pic_url: string | null
  status: string
  start_date: string | null
  end_date: string | null
  notes: string | null
  is_ndt_technician?: boolean
  pcn_no?: string | null
  ndt_qualification?: string | null
  ndt_methods?: string[] | null
  ndt_cert_expiry?: string | null
  created_at: string
  updated_at: string
}

export type EmployeeFile = {
  id: string
  employee_id: string
  file_name: string
  file_path: string
  file_size: number | null
  mime_type: string | null
  category: string | null
  notes: string | null
  uploaded_at: string
}

// --- Billing types ---

export type OrgSettings = {
  id: string
  name: string
  slug: string
  legal_name: string
  address: string | null
  uen: string | null
  logo_url: string | null
  gst_registered: boolean
  gst_rate: number
  default_currency: string
  bank_details: Record<string, any>
  invoice_prefix: string
  quotation_prefix: string
  credit_note_prefix: string
  created_at: string
  updated_at: string
}

export type Customer = {
  id: string
  legal_name: string
  display_name: string | null
  uen: string | null
  address: string | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  default_currency: string
  payment_terms_days: number
  notes: string | null
  created_at: string
  updated_at: string
}

export type DocType = 'quotation' | 'invoice' | 'credit_note'

export type DocumentLine = {
  id: string
  doc_type: DocType
  doc_id: string
  line_no: number
  description: string
  quantity: number
  unit_price: number
  amount: number
}

export type Quotation = {
  id: string
  quotation_no: string | null
  customer_id: string
  issue_date: string | null
  valid_until: string | null
  currency: string
  tax_rate: number
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted'
  notes: string | null
  subtotal: number
  tax_amount: number
  total: number
  converted_invoice_id: string | null
  issued_at: string | null
  issued_snapshot: Record<string, any> | null
  created_at: string
  updated_at: string
}

export type Invoice = {
  id: string
  invoice_no: string | null
  customer_id: string
  issue_date: string | null
  due_date: string | null
  currency: string
  tax_rate: number
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'void'
  notes: string | null
  subtotal: number
  tax_amount: number
  total: number
  source_quotation_id: string | null
  issued_at: string | null
  issued_snapshot: Record<string, any> | null
  import_batch_id: string | null
  created_at: string
  updated_at: string
}

export type CreditNote = {
  id: string
  credit_note_no: string | null
  invoice_id: string
  issue_date: string | null
  currency: string
  tax_rate: number
  reason: string | null
  status: 'draft' | 'issued' | 'void'
  subtotal: number
  tax_amount: number
  total: number
  issued_at: string | null
  issued_snapshot: Record<string, any> | null
  created_at: string
  updated_at: string
}

export type CustomerContact = {
  id: string
  customer_id: string
  name: string
  email: string | null
  phone: string | null
  role: string | null
  is_primary: boolean
  created_at: string
  updated_at: string
}

export type Payment = {
  id: string
  customer_id: string
  received_date: string
  amount: number
  currency: string
  payment_mode: string | null
  reference: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export type PaymentAllocation = {
  id: string
  payment_id: string
  invoice_id: string
  amount_applied: number
  created_at: string
}

export type InvoiceBalance = {
  invoice_id: string
  total: number
  amount_paid: number
  balance_due: number
}

export type AuditLog = {
  id: string
  created_at: string
  actor_user_id: string | null
  actor_email: string | null
  action: string
  table_name: string | null
  row_id: string | null
  before: Record<string, any> | null
  after: Record<string, any> | null
  diff: Record<string, { from: any; to: any }> | null
  reason: string | null
  approval_status: 'not_required' | 'pending' | 'approved' | 'rejected' | 'applied'
  approved_by_user_id: string | null
  approved_by_email: string | null
  approved_at: string | null
  context: Record<string, any> | null
}

export type RtJob = {
  id: string
  renu_ref_no: string
  report_date: string
  day_type: 'Weekday' | 'Weekend' | 'Public Holiday' | null
  client: string | null
  project: string | null
  location: string | null
  piping_structure: string | null
  source_type: string | null
  procedure_no: string | null
  acc_criteria: string | null
  exposure_technique: string | null
  main_code: string | null
  wall_thickness_mm: string | null
  thickness_mm: string | null
  dia_inches: string | null
  film_size: string | null
  film_type: string | null
  welder_ref: string | null
  total_films: number | null
  rejected_films: number
  reshoot_films: number
  chargeable_films: number | null
  mob_count: number
  technicians: string | null
  permit_time_location: string | null
  seatrium_report_no: string | null
  address: string | null
  iqi: string | null
  welding_process: string | null
  viewing: string | null
  material_type: string | null
  curies: string | null
  screen: string | null
  exposure_time: string | null
  surface_condition: string | null
  developing_time: string | null
  films_per_cassette: string | null
  metadata: Record<string, any> | null
  films: Array<{
    ref: string
    welder_ref: string
    section: string
    dia: string
    sod: string
    ofd: string
    density: string
    wall_thk: string
    ug: string
    sensitivity: string
    observation: string
    result: string
  }> | null
  source_file: string | null
  created_at: string
}

export async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const t = data.session?.access_token
  return t ? { Authorization: `Bearer ${t}` } : {}
}
